import React, {useEffect} from "react";
import PropTypes from "prop-types";
import {Route, Switch, withRouter} from "react-router-dom";
import {HomePage} from "pages/home";
import {AssetDetailsPage, AssetEditPage, AssetListPage, AssetNewPage} from "pages/asset";
import {ContractDetailsPage, ContractEditPage, ContractListPage, ContractNewPage} from "pages/contract";
import {DeploymentDetailsPage, DeploymentEditPage, DeploymentListPage, DeploymentNewPage} from "pages/deployment";
import {
    CustomerChangePage,
    CustomerDetailsPage,
    CustomerEditPage,
    CustomerListPage,
    CustomerNewPage,
} from "pages/customer";
import {
    RoomTemplateDetailsPage,
    RoomTemplateEditFilePage,
    RoomTemplateEditPage,
    RoomTemplateNewPage,
} from "pages/room-template";
import {
    EventAgenda,
    EventAlgorithmViewerPage,
    EventDetailsPage,
    EventEditPage,
    EventListPage,
    EventLiveEmisionPage,
    EventNewPage,
    EventCopyPage
} from "pages/event";
import {GraphicsetDetailsPage, GraphicsetEditPage, GraphicsetNewPage} from "pages/graphicset";
import {RecordingDetailsPage, RecordingListPage} from "pages/recording";
import {SourceDetailsPage, SourceEditPage, SourceListPage, SourceNewPage, SourceViewerPage} from "pages/source";
import {UserDetailsPage, UserEditPage, UserListPage, UserNewPage, UserProfilePage} from "pages/user";
import {ChangeLogLevelPage} from "pages/config";
import {permissions, useAuthDataContext, withAdminPermissions, withPermissions} from "components/auth";
import {useCustomThemeContext} from "components/mui-providers";
import Page, {NotFoundPage} from "components/page";
import {useTracking} from "components/tracking";
import {injectIntl} from "react-intl";
import ErrorBoundary from "components/error";
import Dashboard from "components/dashboard";
import LocalRecordsPage from "../../pages/local-records/local-records";

const AppTitleComponent = ({intl, title = ""}) => {
    useEffect(
        () => {
            document.title = `${intl.formatMessage({id: "app.name"})}${title ? " - " : ""}${title}`;
        },
        [intl, title],
    );

    return null;
};

const AppTitle = injectIntl(AppTitleComponent);

const AppFavicon = () => {
    const {customTheme} = useCustomThemeContext();

    useEffect(
        () => {
            const favicon = document.getElementById("favicon");
            if (favicon) {
                favicon.href = customTheme.favicon;
            }
        },
        [customTheme.favicon],
    );

    return null;
};

const RestrictedRoute = ({permissions, component, ...rest}) => (
    <LoggedInRoute component={withPermissions(permissions)(component)} {...rest} />
);

RestrictedRoute.propTypes = {
    /**
     * Permitir el acceso solamente a estos permisos
     */
    permissions: PropTypes.array,
    /*
     * Componente que se renderiza
     */
    component: PropTypes.func,
};

const AdminRoute = ({component, ...rest}) => <LoggedInRoute component={withAdminPermissions(component)} {...rest} />;

AdminRoute.propTypes = {
    /*
     * Componente que se renderiza
     */
    component: PropTypes.func,
};

const LoggedInRoute = ({component, componentProps, action, title, ...options}) => {
    const {user, token} = useAuthDataContext();

    const FinalComponent = injectIntl(withRouter(component));

    useTracking({action});

    return (
        <Route
            {...options}
            component={() => (
                <>
                    <AppFavicon/>
                    <AppTitle title={title}/>
                    <ErrorBoundary user={user} token={token} wrapper={user ? Dashboard : Page}>
                        <FinalComponent {...componentProps} />
                    </ErrorBoundary>
                </>
            )}
        />
    );
};

LoggedInRoute.propTypes = {
    /*
     * Props para el componente página
     */
    componentProps: PropTypes.object,
    /*
     * Nombre de la página
     */
    title: PropTypes.string,
    /*
     * Nombre de la acción a trackear
     */
    action: PropTypes.string,
    /*
     * Componente que se renderiza
     */
    component: PropTypes.func,
    /*
     * Permisos para la página
     */
    permissions: PropTypes.array,
};

const Router = ({intl}) => (
    <Switch>
        <LoggedInRoute
            exact
            path="/"
            component={HomePage}
            action="accesses home page"
            title={intl.formatMessage({id: "app.page.home"})}
        />
        <LoggedInRoute
            path="/home"
            component={HomePage}
            action="accesses home page"
            title={intl.formatMessage({id: "app.page.home"})}
        />
        <AdminRoute
            path="/logger"
            component={ChangeLogLevelPage}
            action="accesses log level configuration page"
            title={intl.formatMessage({id: "app.page.logger"})}
        />
        <RestrictedRoute
            path="/asset/list"
            component={AssetListPage}
            action="accesses assets page"
            title={intl.formatMessage({id: "app.menu.asset.list"})}
            permissions={[permissions.asset_view, permissions.asset_common_view, permissions.graphicset_view]}
        />
        <RestrictedRoute
            path="/asset/details/:id"
            component={AssetDetailsPage}
            action="accesses asset detail"
            title={intl.formatMessage({id: "app.page.asset.details"})}
            permissions={[permissions.asset_view]}
        />
        <RestrictedRoute
            path="/asset/common/details/:id"
            component={AssetDetailsPage}
            action="accesses common asset detail"
            title={intl.formatMessage({id: "app.page.asset.details"})}
            permissions={[permissions.asset_common_view]}
        />
        <RestrictedRoute
            path="/asset/new"
            componentProps={{title: "app.page.asset.new", isCommonAsset: false}}
            component={AssetNewPage}
            action="accesses new asset form"
            title={intl.formatMessage({id: "app.page.asset.new"})}
            permissions={[permissions.asset_manage]}
        />
        <RestrictedRoute
            path="/asset/common/new"
            componentProps={{title: "app.page.asset.new.common", isCommonAsset: true}}
            component={AssetNewPage}
            action="accesses new common asset form"
            title={intl.formatMessage({id: "app.page.asset.new"})}
            permissions={[permissions.asset_common_manage]}
        />
        <RestrictedRoute
            path="/asset/edit/:id"
            component={AssetEditPage}
            action="accesses edit asset form"
            title={intl.formatMessage({id: "app.page.asset.edit"})}
            permissions={[permissions.asset_manage, permissions.asset_common_manage]}
        />
        <LoggedInRoute
            path="/customer/change"
            component={CustomerChangePage}
            action="accesses customer change page"
            title={intl.formatMessage({id: "app.page.customer.change"})}
        />
        <RestrictedRoute
            path="/customer/list"
            component={CustomerListPage}
            action="accesses customers list page"
            title={intl.formatMessage({id: "app.page.customer.list"})}
            permissions={[permissions.customer_view]}
        />
        <RestrictedRoute
            path="/customer/details/:id"
            component={CustomerDetailsPage}
            action="accesses customer details"
            title={intl.formatMessage({id: "app.page.customer.details"})}
            permissions={[permissions.customer_view]}
        />
        <RestrictedRoute
            path="/customer/edit/:id"
            component={CustomerEditPage}
            action="accesses edit customer form"
            title={intl.formatMessage({id: "app.page.customer.edit"})}
            permissions={[permissions.customer_manage]}
        />
        <RestrictedRoute
            path="/customer/new"
            component={CustomerNewPage}
            action="accesses new customer form"
            title={intl.formatMessage({id: "app.page.customer.new"})}
            permissions={[permissions.customer_manage]}
        />
        <RestrictedRoute
            path="/contract/list"
            component={ContractListPage}
            action="accesses contract list"
            title={intl.formatMessage({id: "app.page.contract.list"})}
            permissions={[permissions.contract_view]}
        />
        <RestrictedRoute
            path="/contract/details/:id"
            component={ContractDetailsPage}
            action="accesses contract details"
            title={intl.formatMessage({id: "app.page.contract.details"})}
            permissions={[permissions.contract_view]}
        />
        <RestrictedRoute
            path="/contract/edit/:id"
            component={ContractEditPage}
            action="accesses edit contract form"
            title={intl.formatMessage({id: "app.page.contract.edit"})}
            permissions={[permissions.contract_manage]}
        />
        <RestrictedRoute
            path="/contract/new"
            component={ContractNewPage}
            action="accesses new contract form"
            title={intl.formatMessage({id: "app.page.contract.new"})}
            permissions={[permissions.contract_manage]}
        />
        <RestrictedRoute
            path="/roomTemplate/details/:id/contractId/:contractId"
            component={RoomTemplateDetailsPage}
            action="accesses roomTemplate details page"
            title={intl.formatMessage({id: "app.page.roomTemplate.details"})}
            permissions={[permissions.roomtemplate_view]}
        />
        <RestrictedRoute
            path="/roomTemplate/file-editor/:id/contractId/:contractId"
            component={RoomTemplateEditFilePage}
            action="accesses roomTemplate file editor"
            title={intl.formatMessage({id: "app.page.roomTemplate.editFile"})}
            permissions={[permissions.roomtemplate_manage]}
        />
        <RestrictedRoute
            path="/roomTemplate/new/contractId/:contractId"
            component={RoomTemplateNewPage}
            action="accesses new roomTemplate form"
            title={intl.formatMessage({id: "app.page.roomTemplate.new"})}
            permissions={[permissions.roomtemplate_manage]}
        />
        <RestrictedRoute
            path="/roomTemplate/edit/:id/contractId/:contractId"
            component={RoomTemplateEditPage}
            action="accesses edit roomTemplate form"
            title={intl.formatMessage({id: "app.page.roomTemplate.edit.appbar"})}
            permissions={[permissions.roomtemplate_manage]}
        />
        <RestrictedRoute
            path="/deployment/list"
            component={DeploymentListPage}
            action="accesses deployment list"
            title={intl.formatMessage({id: "app.page.deployment.list"})}
            permissions={[permissions.deployment_view]}
        />
        <RestrictedRoute
            path="/deployment/details/:id"
            component={DeploymentDetailsPage}
            action="accesses deployment details"
            title={intl.formatMessage({id: "app.page.deployment"})}
            permissions={[permissions.deployment_view]}
        />
        <RestrictedRoute
            exact
            path="/deployment/new"
            component={DeploymentNewPage}
            action="accesses new deployment form"
            title={intl.formatMessage({id: "app.page.deployment.new"})}
            permissions={[permissions.deployment_manage]}
        />
        <RestrictedRoute
            exact
            path="/deployment/edit/:id"
            component={DeploymentEditPage}
            action="accesses edit deployment form"
            title={intl.formatMessage({id: "app.page.deployment.edit"})}
            permissions={[permissions.deployment_manage]}
        />
        <RestrictedRoute
            path="/event/list"
            component={EventListPage}
            action="accesses event list"
            title={intl.formatMessage({id: "app.page.event.list"})}
            permissions={[permissions.event_view]}
        />
        <RestrictedRoute
            path="/event/agenda"
            component={EventAgenda}
            action="accesses event agenda"
            title={intl.formatMessage({id: "app.page.event.agenda"})}
            permissions={[permissions.event_view]}
        />
        <RestrictedRoute
            exact
            path="/event/details/:id"
            component={EventDetailsPage}
            action="accesses event details"
            title={intl.formatMessage({id: "app.page.event"})}
            permissions={[permissions.event_view]}
        />
        <RestrictedRoute
            path="/event/live-emision/:id/customer/:customerId"
            component={EventLiveEmisionPage}
            action="accesses live event emission"
            title={intl.formatMessage({id: "app.page.event.liveEmision"})}
            permissions={[permissions.event_view]}
        />
        <RestrictedRoute
            path="/event/algorithm-viewer/:id/customer/:customerId"
            component={EventAlgorithmViewerPage}
            action="accesses IA depuration viewer"
            title={intl.formatMessage({id: "app.page.event.algorithmViewer"})}
            permissions={[permissions.event_view]}
        />
        <RestrictedRoute
            path="/event/details/:id/customer/:customerId"
            component={EventDetailsPage}
            action="accesses event details"
            title={intl.formatMessage({id: "app.page.event"})}
            permissions={[permissions.event_view]}
        />
        <RestrictedRoute
            path="/event/new"
            component={EventNewPage}
            action="accesses new event form"
            title={intl.formatMessage({id: "app.page.event.new"})}
            permissions={[permissions.event_manage]}
        />
        <RestrictedRoute
            exact
            path="/event/edit/:id"
            component={EventEditPage}
            action="accesses edit event form"
            title={intl.formatMessage({id: "app.page.event.edit.appbar"})}
            permissions={[permissions.event_manage]}
        />
        <RestrictedRoute
            path="/event/edit/:id/customer/:customerId"
            component={EventEditPage}
            action="accesses edit event form"
            title={intl.formatMessage({id: "app.page.event.edit.appbar"})}
            permissions={[permissions.event_manage]}
        />

        <RestrictedRoute
            exact
            path="/event/copy/:id"
            component={EventCopyPage}
            action="accesses edit event form"
            title={intl.formatMessage({id: "app.page.event.edit.appbar"})}
            permissions={[permissions.event_manage]}
        />
        <RestrictedRoute
            path="/event/copy/:id/customer/:customerId"
            component={EventCopyPage}
            action="accesses edit event form"
            title={intl.formatMessage({id: "app.page.event.edit.appbar"})}
            permissions={[permissions.event_manage]}
        />

        <RestrictedRoute
            path="/graphicset/details/:id/customerId/:customerId"
            component={GraphicsetDetailsPage}
            action="accesses graphicset details"
            title={intl.formatMessage({id: "app.page.graphicset"})}
            permissions={[permissions.graphicset_view]}
        />
        <RestrictedRoute
            path="/graphicset/edit/:id/customerId/:customerId"
            component={GraphicsetEditPage}
            action="accesses edit graphicset form"
            title={intl.formatMessage({id: "app.page.graphicset.edit"})}
            permissions={[permissions.graphicset_manage]}
        />
        <RestrictedRoute
            exact
            path="/graphicset/new"
            component={GraphicsetNewPage}
            action="accesses new graphicset form"
            title={intl.formatMessage({id: "app.page.graphicset.new"})}
            permissions={[permissions.graphicset_manage]}
        />
        <RestrictedRoute
            path="/graphicset/new/:id/customerId/:customerId"
            component={GraphicsetNewPage}
            action="accesses copy graphicset form"
            title={intl.formatMessage({id: "app.page.graphicset.new"})}
            permissions={[permissions.graphicset_manage]}
        />
        <RestrictedRoute
            path="/recording/list"
            component={RecordingListPage}
            action="accesses recordings list"
            title={intl.formatMessage({id: "app.page.recording.list"})}
            permissions={[permissions.recording_view]}
        />
        <RestrictedRoute
            path="/recording/details/:id"
            component={RecordingDetailsPage}
            action="accesses recording details"
            title={intl.formatMessage({id: "app.page.recording.details.appbar"})}
            permissions={[permissions.recording_view]}
        />
        <RestrictedRoute
            exact
            path="/source/list"
            component={SourceListPage}
            action="accesses source list"
            title={intl.formatMessage({id: "app.page.source.list"})}
            permissions={[permissions.source_view]}
        />
        <RestrictedRoute
            exact
            path="/source/new"
            component={SourceNewPage}
            action="accesses new source form"
            title={intl.formatMessage({id: "app.page.source.new"})}
            permissions={[permissions.source_manage]}
        />
        <RestrictedRoute
            exact
            path="/source/details/:id"
            component={SourceDetailsPage}
            action="accesses source details"
            title={intl.formatMessage({id: "app.page.source.details.appbar"})}
            permissions={[permissions.source_view]}
        />
        <RestrictedRoute
            exact
            path="/source/details/:id/disks/"
            component={LocalRecordsPage}
            action="accesses source camera disks"
            title={intl.formatMessage({id: "app.page.source.details.appbar"})}
            permissions={[permissions.source_view]}
        />
        <RestrictedRoute
            exact
            path="/source/viewer/:id"
            component={SourceViewerPage}
            action="accesses source viewer"
            title={intl.formatMessage({id: "app.page.source.viewer"})}
            permissions={[permissions.source_view]}
        />
        <RestrictedRoute
            exact
            path="/source/edit/:id"
            action="accesses edit source form"
            component={SourceEditPage}
            title={intl.formatMessage({id: "app.page.source.edit"})}
            permissions={[permissions.source_manage]}
        />
        <RestrictedRoute
            path="/user/list"
            action="accesses user list"
            component={UserListPage}
            title={intl.formatMessage({id: "app.page.user.list"})}
            permissions={[permissions.user_view]}
        />
        <RestrictedRoute
            path="/user/details/:username"
            action="accesses user details"
            component={UserDetailsPage}
            title={intl.formatMessage({id: "app.page.user.details.appbar"})}
            permissions={[permissions.user_view]}
        />
        <LoggedInRoute
            path="/user/profile"
            action="accesses to own profile"
            component={UserProfilePage}
            title={intl.formatMessage({id: "app.page.user.profile"})}
        />
        <RestrictedRoute
            exact
            path="/user/new"
            action="accesses to new user form"
            component={UserNewPage}
            title={intl.formatMessage({id: "app.page.user.new"})}
            permissions={[permissions.user_manage]}
        />
        <RestrictedRoute
            exact
            path="/user/edit/:username"
            action="accesses to edit user form"
            component={UserEditPage}
            title={intl.formatMessage({id: "app.page.user.edit"})}
            permissions={[permissions.user_manage]}
        />
        <LoggedInRoute
            component={NotFoundPage}
            action="accesses to 404 Not Found"
            title={intl.formatMessage({id: "app.page.404.appbar"})}
        />
    </Switch>
);

export default injectIntl(Router);
